import { useEffect, useMemo, useState } from "react";
import { GroupDescription, RecipientsSectionHeader, Title } from "./texts";
import { SearchBar } from "./searchBar";
import { MailboxGrid } from "./mailboxGrid";
import { MailboxDetails } from "../../entities";

type PageContentProp = {
  groupDescription: string | undefined;
  mailboxes: MailboxDetails[];
};

/**
 * Content of mailbox group page.
 *
 * @param mailboxes list of mailboxes in a group
 * @returns a JSX element containing descriptive texts about the page and / or group,
 *  search bar to search for specifix mailbox and grid view of mailboxes.
 */
export const PageContent = ({
  mailboxes,
  groupDescription,
}: PageContentProp) => {
  // ==========================================================================
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const sortedMailboxes = useMemo(
    () =>
      [...mailboxes].sort((a, b) => {
        return (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
      }),
    [mailboxes]
  );

  const filteredMailboxes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedMailboxes;

    // Break query into sub queries, e.g. ["cortez","anna"]
    const queryParts = q.split(/\s+/);

    // Map to { mailbox, score } or null if it doesn't match all parts
    const scored = sortedMailboxes
      .map((mailbox) => {
        const name = mailbox.name?.toLowerCase();
        const email = mailbox.email.toLowerCase();

        // Must match every query part in either name or email
        const matchesAll = queryParts.every(
          (part) => name?.includes(part) || email.includes(part)
        );
        if (!matchesAll) return null;

        // Compute a score per part.
        //
        // Name >> email (cuz user mostly search for the name of the person as it is easy to remember name than emails)
        // Name or email begins with query >> Name or email includes
        let score = 0;
        for (const part of queryParts) {
          if (name?.startsWith(part)) {
            score += 100;
          } else if (name?.includes(part)) {
            score += 50;
          }

          if (email.startsWith(part)) {
            score += 20;
          } else if (email.includes(part)) {
            score += 10;
          }
        }

        return { mailbox: mailbox, score };
      })
      .filter(
        (entry): entry is { mailbox: MailboxDetails; score: number } =>
          entry !== null
      )
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.mailbox);

    return scored;
  }, [searchQuery, sortedMailboxes]);

  // ==========================================================================

  const [showAll, setShowAll] = useState(filteredMailboxes.length <= 8);

  const allMailboxes = filteredMailboxes;
  const visibleMailboxes = showAll ? allMailboxes : allMailboxes.slice(0, 8);

  useEffect(() => {
    if (filteredMailboxes.length <= 8) {
      setShowAll(true);
      return;
    }
    if (!isExpanded && searchQuery) {
      setShowAll(true);
    } else if (!isExpanded && !searchQuery) {
      setShowAll(false);
    }
  }, [searchQuery, isExpanded, filteredMailboxes]);

  //
  //
  //
  return (
    <div className="mx-auto flex w-full max-w-[500px] flex-col px-4 sm:px-0">
      <Title classname="self-start" />
      <div className="h-3" />
      <GroupDescription classname="self-start" text={groupDescription} />
      <div className="h-7" />
      <RecipientsSectionHeader classname="self-start" />
      <div className="h-5" />
      <SearchBar
        searchQuery={searchQuery}
        onQueryChange={(newQuery) => setSearchQuery(newQuery)}
      />
      <div className="h-5" />
      <MailboxGrid
        mailboxes={visibleMailboxes}
        showAllMailboxes={showAll}
        onExpanded={() => {
          setShowAll(true);
          setExpanded(true);
        }}
      />
    </div>
  );
};
